import { useEffect, useMemo, useState } from "react";
import "./onehealth.css";
import { LangProvider, useLang } from "./OneHealthContext";
import { LTBrand, PoweredByCarePlix, VerdictBadge } from "./components";
import Pulmo from "./steps/Pulmo";
import { createToken } from "./careplix";
import { commonEmbedParams, deliverToHost } from "./embedBridge";
import { LANGS } from "./i18n";

/* ============================================================
   EMBED / WEBVIEW MODE — standalone PULMO (lung) scan journey.

   URL:  /embed/pulmoscan?employee_id=EMP123[&lang=en][&redirect_url=...][&host_origin=...]

   No biometrics needed — the pulmo journey is cough + vowel audio plus the
   CarePlix survey. A pulmo-enabled scan session is created on load
   (create-token with is_pulmo_required), then the standard Pulmo screen runs.

   Result message → { type: "careplix.pulmoscan.result", score, fevFvc, oad, ... }
   delivered over the same channels as the face-scan embed (postMessage /
   ReactNativeWebView / OneHealthBridge / OneHealthChannel / WKWebView handler /
   optional redirect_url?result=<base64url JSON>).
   ============================================================ */

function EmbedFlow() {
  const { tr, dir, setLang } = useLang();
  const p = useMemo(() => commonEmbedParams(new URLSearchParams(window.location.search)), []);
  const [scanToken, setScanToken] = useState(null);
  const [bootError, setBootError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (p.lang && LANGS.some((l) => l.code === p.lang)) setLang(p.lang);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Create a pulmo-enabled scan session, then run the standard Pulmo journey.
  useEffect(() => {
    let aborted = false;
    setBootError(null);
    (async () => {
      try {
        const tj = await createToken({ employee_id: p.employeeId, is_pulmo_required: true });
        if (!tj?.scan_token) throw new Error("create-token failed");
        if (!aborted) setScanToken(tj.scan_token);
      } catch (err) {
        console.error("pulmo embed create-token failed", err);
        if (!aborted) setBootError(tr("scan.errStartProper"));
      }
    })();
    return () => { aborted = true; };
  }, [retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPulmoDone = (pr) => {
    const message = {
      type: "careplix.pulmoscan.result",
      ok: true,
      employee_id: p.employeeId,
      scan_token: scanToken,
      score: pr.score,
      fevFvc: pr.fevFvc ?? null,
      oad: pr.oad ?? null,
      completed_at: new Date().toISOString(),
    };
    deliverToHost(message, p.redirectUrl, p.hostOrigin);
    setDone(message);
  };

  let screen;
  if (bootError) {
    screen = (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center", padding: 28 }}>
        <h2 style={{ marginBottom: 10 }}>⚠️</h2>
        <p className="oh-foot">{bootError}</p>
        <button className="oh-btn" style={{ marginTop: 18, maxWidth: 320 }}
          onClick={() => { setScanToken(null); setRetryKey((k) => k + 1); }}>
          {tr("common.tryAgain")}
        </button>
      </div>
    );
  } else if (done) {
    screen = (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center", padding: 28 }}>
        <VerdictBadge status={done.score >= 70 ? "green" : "amber"} />
        <h2 style={{ marginTop: 16 }}>Scan complete</h2>
        <p className="oh-foot" style={{ marginTop: 10 }}>
          The lung screening result has been sent back to the app. You can close this screen.
        </p>
      </div>
    );
  } else if (!scanToken) {
    screen = (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center", padding: 28 }}>
        <div className="oh-spin" />
        <p className="oh-foot" style={{ marginTop: 14 }}>{tr("scan.opening")}</p>
      </div>
    );
  } else {
    screen = <Pulmo scanToken={scanToken} onDone={onPulmoDone} />;
  }

  return (
    <div className="oh-stage" dir={dir}>
      <div className="oh-device">
        <div className="oh-brandtop">
          <LTBrand />
          <PoweredByCarePlix />
        </div>
        <div className="oh-screen">{screen}</div>
      </div>
    </div>
  );
}

export default function EmbedPulmoScan() {
  return (
    <LangProvider>
      <EmbedFlow />
    </LangProvider>
  );
}
